import { useContext, useMemo } from 'react';
import { UserContext } from '@/components/App';
import { CorpAdminContext } from '../store';
import { MARKETPLACE_KEY } from '../utils';
import {
    accountCarrierEntries,
    buildCarrierRows,
    flattenCarrierAssetMaps,
    flexCentersFor,
    groupShippersBySettings,
    isFlexCenterRow,
    linkedShippersByCarrierFrom,
    MARKETPLACE_ROW,
    marketplaceShippersFor,
    pricingSummaryOf,
    shipperSettingsFor,
} from './utils';

export const useCarrierRows = () => {
    const { admin_clients } = useContext(UserContext);
    const { state } = useContext(CorpAdminContext);
    const {
        assetHeavy,
        assetLight,
        defaultOverridesByCarrier,
        flexCenterPricingByContractor,
        defaultIntegrationsByCarrier,
        capacityByCarrier,
        contractors,
        corpDocsByCarrier,
        rateAddendumsByCarrierShipper,
        assignmentsByShipper,
        tariffs,
        loading,
        error,
    } = state;

    const clientsById = useMemo(
        () => Object.fromEntries((admin_clients || []).map((client) => [client.client_id, client])),
        [admin_clients]
    );

    const carrierEntries = useMemo(() => {
        const overrideEntries = flattenCarrierAssetMaps([assetHeavy, assetLight]);
        const defaultCarriers = [
            ...Object.values(defaultIntegrationsByCarrier).map(
                ([override]) => override.carrier || override.middle_mile_carrier
            ),
            ...Object.values(defaultOverridesByCarrier).map(([override]) => override.client),
        ];

        return accountCarrierEntries({ carrierEntries: overrideEntries, contractors, defaultCarriers });
    }, [assetHeavy, assetLight, contractors, defaultIntegrationsByCarrier, defaultOverridesByCarrier]);

    const rows = useMemo(() => {
        const integrationsByCarrier = {};
        const documentsByCarrier = {};

        for (const { carrier, integrations } of carrierEntries) {
            const docsByType = corpDocsByCarrier[carrier.client_id] || {};
            integrationsByCarrier[carrier.client_id] = integrations;
            documentsByCarrier[carrier.client_id] = Object.values(docsByType);
        }

        const carriers = carrierEntries.map(({ carrier }) => carrier);
        const orgClientIds = Object.keys(clientsById);
        const contractorIds = contractors.map((contractor) => contractor.client_id);
        const carrierRows = buildCarrierRows({
            carriers,
            orgClientIds,
            contractorIds,
            integrationsByCarrier,
            documentsByCarrier,
        });

        return [MARKETPLACE_ROW, ...carrierRows];
    }, [carrierEntries, clientsById, contractors, corpDocsByCarrier]);

    const tariffsById = useMemo(
        () => Object.fromEntries((tariffs || []).map((tariff) => [tariff.tariff_id, tariff])),
        [tariffs]
    );

    const defaultPricingByCarrier = useMemo(() => {
        const summaries = {};

        for (const { carrier } of carrierEntries) {
            const overrides = defaultOverridesByCarrier?.[carrier.client_id] || [];
            const summary = pricingSummaryOf(overrides, tariffsById);
            summaries[carrier.client_id] = { overrides, ...summary };
        }

        return summaries;
    }, [carrierEntries, defaultOverridesByCarrier, tariffsById]);

    const shipperGroupsByCarrier = useMemo(() => {
        const linkedShippersByCarrier = linkedShippersByCarrierFrom(assignmentsByShipper, clientsById);
        const groupsByCarrier = {};

        for (const { carrier, settingsByShipper } of carrierEntries) {
            const shippers = shipperSettingsFor({
                carrierId: carrier.client_id,
                linkedShippers: linkedShippersByCarrier[carrier.client_id],
                settingsByShipper,
                assignmentsByShipper,
                addendumsByShipper: rateAddendumsByCarrierShipper?.[carrier.client_id],
                capacityShipperIds: capacityByCarrier?.[carrier.client_id],
                tariffsById,
                clientsById,
                defaultIntegrations: defaultIntegrationsByCarrier[carrier.client_id],
            });
            groupsByCarrier[carrier.client_id] = groupShippersBySettings(shippers, {
                defaultPricing: defaultPricingByCarrier[carrier.client_id],
            });
        }

        return groupsByCarrier;
    }, [
        carrierEntries,
        assignmentsByShipper,
        clientsById,
        tariffsById,
        rateAddendumsByCarrierShipper,
        capacityByCarrier,
        defaultPricingByCarrier,
        defaultIntegrationsByCarrier,
    ]);

    const marketplaceShippers = useMemo(() => {
        const { carrier: _marketplaceCarrier, ...settingsByShipper } = assetLight[MARKETPLACE_KEY];

        return marketplaceShippersFor({ settingsByShipper, assignmentsByShipper, tariffsById, clientsById });
    }, [assetLight, assignmentsByShipper, tariffsById, clientsById]);

    const flexCentersByContractor = useMemo(
        () =>
            contractors.reduce((byContractor, partnership) => {
                if (!partnership.contractor) return byContractor;

                byContractor[partnership.client_id] = flexCentersFor({
                    flexCenters: partnership.flexCenters,
                    pricingByFlexCenter: flexCenterPricingByContractor[partnership.client_id],
                    tariffsById,
                    defaultPricing: defaultPricingByCarrier[partnership.client_id],
                });

                return byContractor;
            }, {}),
        [contractors, flexCenterPricingByContractor, tariffsById, defaultPricingByCarrier]
    );

    const contractorsByFlexCenter = useMemo(
        () =>
            rows.reduce((byFlexCenter, row) => {
                // Empty rather than absent, so a Flex Center with no contractors still shows the section.
                if (isFlexCenterRow(row)) byFlexCenter[row.client_id] ??= [];
                if (!row.isContractor) return byFlexCenter;

                for (const flexCenter of flexCentersByContractor[row.client_id] || []) {
                    const defaultPricing = defaultPricingByCarrier[row.client_id];
                    (byFlexCenter[flexCenter.client_id] ??= []).push({
                        contractorRow: row,
                        flexCenter,
                        defaultPricing,
                    });
                }

                return byFlexCenter;
            }, {}),
        [rows, flexCentersByContractor, defaultPricingByCarrier]
    );

    return {
        rows,
        shipperGroupsByCarrier,
        marketplaceShippers,
        flexCentersByContractor,
        contractorsByFlexCenter,
        defaultPricingByCarrier,
        defaultIntegrationsByCarrier,
        loading: !!loading,
        error,
    };
};
